import { Component, h, Prop } from '@stencil/core';
import { __ } from '@wordpress/i18n';

import { state as checkoutState } from '@store/checkout';
import { LineItem, Product, Variant } from '../../../../types';
import { removeCheckoutLineItem, updateCheckoutLineItem } from '@store/checkout/mutations';
import { formBusy } from '@store/form/getters';
import { getMaxStockQuantity } from '../../../../functions/quantity';
import { groupBundleLineItems } from '../../../../functions/line-items';

/**
 * @part base - The component base
 * @part line-item - The line item
 * @part product-line-item - The product line item
 * @part line-item__image - The line item image
 * @part line-item__text - The line item text
 * @part line-item__title - The line item title
 * @part line-item__suffix - The line item suffix
 * @part line-item__description - The line item description (variant, price name, SKU)
 * @part line-item__trial-fees - The line item trial and fees
 * @part line-item__price - The line item price
 * @part line-item__price-amount - The line item price amount
 * @part line-item__price-description - The line item price description
 * @part line-item__price-scratch - The line item price scratch
 * @part line-item__static-quantity - The line item static quantity
 * @part line-item__remove-icon - The line item remove icon
 * @part line-item__quantity - The line item quantity
 * @part line-item__quantity-minus - The line item quantity minus
 * @part line-item__quantity-minus-icon - The line item quantity minus icon
 * @part line-item__quantity-plus - The line item quantity plus
 * @part line-item__quantity-plus-icon - The line item quantity plus icon
 * @part line-item__quantity-input - The line item quantity input
 * @part line-item__details - The collapsible details region (bundle components)
 * @part line-item__details-component - A single bundle component row
 * @part line-item__details-variant - The variant options within a bundle component row
 * @part line-item__details-toggle - The details expand/collapse toggle button
 * @part line-item__note - The line item note
 */

@Component({
  tag: 'sc-line-items',
  styleUrl: 'sc-line-items.scss',
  shadow: true,
})
export class ScLineItems {
  /**
   * Is the line item editable?
   */
  @Prop() editable: boolean;

  /**
   * Is the line item removable?
   */
  @Prop() removable: boolean;

  /**
   * Show every bundle component (default), or only those with a selected
   * variant when set to `false`.
   */
  @Prop() showAllBundleItems: boolean = true;

  /** Separator between a bundle component's name and its variant options. */
  @Prop() separator: string = '·';

  /**
   * Is the line item editable?
   */
  isEditable(item: LineItem) {
    // ad_hoc prices and bumps cannot have quantity.
    if (item?.price?.ad_hoc || item?.bump_amount || item?.locked) {
      return false;
    }
    return this.editable;
  }

  render() {
    if (!!formBusy() && !checkoutState?.checkout?.line_items?.data?.length) {
      return (
        <sc-line-item>
          <sc-skeleton style={{ 'width': '50px', 'height': '50px', '--border-radius': '0' }} slot="image"></sc-skeleton>
          <sc-skeleton slot="title" style={{ width: '120px', display: 'inline-block' }}></sc-skeleton>
          <sc-skeleton slot="description" style={{ width: '60px', display: 'inline-block' }}></sc-skeleton>
          <sc-skeleton style={{ width: '120px', display: 'inline-block' }} slot="price"></sc-skeleton>
          <sc-skeleton style={{ width: '60px', display: 'inline-block' }} slot="price-description"></sc-skeleton>
        </sc-line-item>
      );
    }

    // Sort items so that items with swaps are at the top.
    const sortedItems = (checkoutState?.checkout?.line_items?.data || []).sort((a, b) => {
      const aHasSwap = a?.is_swappable ? 1 : 0;
      const bHasSwap = b?.is_swappable ? 1 : 0;
      return bHasSwap - aHasSwap;
    });

    // Group bundle parents and their components, then render parents first.
    const { regular, bundleParents, componentsByParent } = groupBundleLineItems(sortedItems as LineItem[]);
    const orderedItems = [...bundleParents, ...regular];

    return (
      <div class="line-items" part="base" tabindex="0">
        {orderedItems.map(item => {
          const product = item?.price?.product as Product;
          const isBundle = !!product?.bundle;
          const max = getMaxStockQuantity(product, item?.variant as Variant);
          return (
            <div class={`line-item ${item?.is_swappable ? 'line-item--has-swap' : ''}`} key={item.id}>
              <sc-product-line-item
                image={item?.image}
                name={product?.name}
                price={item?.price?.name}
                variant={item?.variant_display_options}
                fees={item?.fees?.data}
                amount={item.ad_hoc_display_amount ? item.ad_hoc_display_amount : item.subtotal_display_amount}
                scratch={item.ad_hoc_display_amount ? null : item?.scratch_display_amount}
                trial={item?.price?.trial_text}
                interval={`${item?.price?.short_interval_text} ${item?.price?.short_interval_count_text}`}
                quantity={item.quantity}
                purchasableStatus={item?.purchasable_status_display}
                note={item?.display_note}
                bundleComponents={isBundle ? componentsByParent[item.id] || [] : []}
                showAllBundleItems={this.showAllBundleItems}
                separator={this.separator}
                {...(max ? { max } : {})}
                editable={this.isEditable(item)}
                removable={!item?.locked && this.removable}
                onScUpdateQuantity={e => updateCheckoutLineItem({ id: item.id, data: { quantity: e.detail } })}
                onScRemove={() => removeCheckoutLineItem(item?.id)}
                exportparts="base:line-item, product-line-item, image:line-item__image, placeholder__image: line-item__placeholder-image, text:line-item__text, title:line-item__title, suffix:line-item__suffix, description:line-item__description, trial-fees:line-item__trial-fees, price:line-item__price, price__amount:line-item__price-amount, price__description:line-item__price-description, price__scratch:line-item__price-scratch, static-quantity:line-item__static-quantity, remove-icon__base:line-item__remove-icon, quantity:line-item__quantity, quantity__minus:line-item__quantity-minus, quantity__minus-icon:line-item__quantity-minus-icon, quantity__plus:line-item__quantity-plus, quantity__plus-icon:line-item__quantity-plus-icon, quantity__input:line-item__quantity-input, details:line-item__details, details__component:line-item__details-component, details__variant:line-item__details-variant, details__toggle:line-item__details-toggle, note:line-item__note"
              />
              {!isBundle && <sc-swap lineItem={item} />}
            </div>
          );
        })}
      </div>
    );
  }
}
